import { HubConnectionBuilder, HubConnectionState, LogLevel, type HubConnection } from '@microsoft/signalr'
import { sessionExpired } from './api'

/**
 * Esperas entre reintentos; después del último se sigue cada 10 s, sin rendirse. Con 30 s la
 * vuelta tras reiniciar la API tardaba 40 s en un Centro de Mando que vigila en vivo.
 */
const REINTENTOS = [0, 2000, 5000, 10000]

/** El servidor rechazó el token: reintentar no sirve, hay que volver a ingresar. */
export function isUnauthorized(error: unknown): boolean {
  const e = error as { statusCode?: number; message?: string } | undefined
  return e?.statusCode === 401 || /\b401\b/.test(e?.message ?? '')
}

/** Los estados del canal; las pantallas los traducen con `link.<estado>`. */
export const LINK_STATES = ['connecting', 'live', 'offline'] as const
export type LinkState = typeof LINK_STATES[number]

export interface RealtimeHandlers {
  onUnitChanged: (unit: unknown) => void
  onAlertChanged: (alert: unknown) => void
  onReoptimizationReady: (outcome: unknown) => void
  onState: (state: LinkState) => void
}

/**
 * Canal del Centro de Mando (`/hubs/command`, ADR-0002).
 *
 * Los avisos traen el dato, pero la verdad sigue siendo la API: al (re)conectar se vuelve a
 * consultar todo, así un aviso perdido mientras el canal estaba caído no deja la pantalla vieja.
 */
export function connectRealtime(token: () => string | null, handlers: RealtimeHandlers, onResync: () => void): HubConnection {
  const connection = new HubConnectionBuilder()
    .withUrl('/hubs/command', { accessTokenFactory: () => token() ?? '' })
    // Un Centro de Mando no se rinde: tras un reinicio largo de la API seguía «Sin conexión» hasta
    // recargar a mano. Pero con un 401 (token vencido, o la API de desarrollo que cambió su llave)
    // reintentar es inútil: decía «Reconectando» para siempre; ahora lleva a volver a ingresar.
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: (ctx) => {
        if (isUnauthorized(ctx.retryReason)) {
          sessionExpired()
          return null
        }
        return REINTENTOS[ctx.previousRetryCount] ?? 10000
      },
    })
    .configureLogging(LogLevel.Warning)
    .build()

  connection.on('UnitChanged', handlers.onUnitChanged)
  connection.on('AlertChanged', handlers.onAlertChanged)
  connection.on('ReoptimizationReady', handlers.onReoptimizationReady)

  connection.onreconnecting(() => handlers.onState('connecting'))
  connection.onreconnected(() => {
    handlers.onState('live')
    onResync()
  })
  connection.onclose(() => handlers.onState('offline'))

  handlers.onState('connecting')
  connection
    .start()
    .then(() => {
      handlers.onState('live')
      onResync()
    })
    .catch((error: unknown) => {
      if (isUnauthorized(error)) sessionExpired()
      else handlers.onState('offline')
    })

  return connection
}

export function stopRealtime(connection: HubConnection | null) {
  if (connection && connection.state !== HubConnectionState.Disconnected) void connection.stop()
}
